import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {Box, Button, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';
import {CheckCircleOutlined, ErrorOutlineOutlined, ScheduleOutlined, ChatOutlined} from 'views/icons';
import {useNavigate} from 'react-router-dom';


const RoutinesView = (props) => {
  const {routine, onRunNow, onEdit, onToggleStatus} = props;
  const navigate = useNavigate();

  const SCHEDULE_LABEL = {
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    custom: 'Custom',
    one_time: 'One-time',
  };

  const DOW = {SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6};

  const computeNextRunAt = (schedule, lastRunAt) => {
    if (!schedule) return null;
    const type = (schedule.type || '').toUpperCase();
    const now = moment();

    switch (type) {
      case 'ONE_TIME': {
        if (!schedule.datetime) return null;
        const fireAt = moment(schedule.datetime + 'Z');
        return fireAt.isAfter(now) ? fireAt.toISOString() : null;
      }
      case 'HOURLY': {
        const candidate = now.clone().startOf('hour').minute(schedule.minute ?? 0);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'hour').toISOString();
      }
      case 'DAILY': {
        if (!schedule.time) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const candidate = now.clone().startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'day').toISOString();
      }
      case 'WEEKLY': {
        if (!schedule.time || !schedule.dayOfWeek) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const targetDow = DOW[(schedule.dayOfWeek || '').toUpperCase()] ?? 1;
        const candidate = now.clone().day(targetDow).startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'week').toISOString();
      }
      case 'MONTHLY': {
        if (!schedule.time || !schedule.dayOfMonth) return null;
        const [h, m] = schedule.time.split(':').map(Number);
        const candidate = now.clone().date(schedule.dayOfMonth).startOf('day').hour(h).minute(m);
        if (candidate.isAfter(now)) return candidate.toISOString();
        return candidate.add(1, 'month').toISOString();
      }
      case 'CUSTOM': {
        const match = (schedule.expression || '').match(/^rate\((\d+)\s+(minutes?|hours?|days?)\)$/i);
        if (match) {
          const n = parseInt(match[1], 10);
          const unit = match[2].toLowerCase().startsWith('day') ? 'days' :
            match[2].toLowerCase().startsWith('hour') ? 'hours' : 'minutes';
          return (lastRunAt ? moment(lastRunAt) : now).clone().add(n, unit).toISOString();
        }
        return null;
      }
      default:
        return null;
    }
  };

  const formatTs = (ts) => {
    if (!ts) return null;
    const m = moment(ts);
    return m.isValid() ? m.format('MMM D, YYYY, HH:mm') : ts;
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return '—';
    const type = (schedule.type || '').toLowerCase();
    const base = SCHEDULE_LABEL[type] || schedule.type;
    if (type === 'one_time') {
      if (!schedule.datetime) return base;
      return `Once on ${moment(schedule.datetime + 'Z').format('MMM D, YYYY, HH:mm')}`;
    }
    if (type === 'daily') return `${base} at ${schedule.time || '?'}`;
    if (type === 'weekly') return `${base} (${schedule.dayOfWeek}) at ${schedule.time || '?'}`;
    if (type === 'monthly') return `${base} on day ${schedule.dayOfMonth} at ${schedule.time || '?'}`;
    if (type === 'hourly') return schedule.minute != null ? `${base} at :${String(schedule.minute).padStart(2, '0')}` : base;
    if (type === 'custom') return schedule.expression || base;
    return base;
  };

  if (!routine) {
    return (
      <Box sx={{width: '100%', p: '40px', boxSizing: 'border-box', backgroundColor: '#FFFFFF',
        display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#A8A49E', lineHeight: '150%'}}>
          <Translate expression='routines.View.Empty' />
        </Typography>
      </Box>
    );
  }

  const isActive = routine.status === 'ACTIVE';
  const history = Array.isArray(routine.history) ? routine.history : [];
  const lastRan = formatTs(routine.lastRunAt);
  const nextRun = routine.status === 'ACTIVE' ?
    formatTs(computeNextRunAt(routine.schedule, routine.lastRunAt)) :
    null;

  return (
    <Box sx={{width: '100%', p: '40px', boxSizing: 'border-box', backgroundColor: '#FFFFFF', overflowY: 'auto'}}>
      <Stack sx={{gap: '24px'}}>
        {/* Header */}
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' sx={{gap: '24px', flexWrap: 'wrap'}}>
          <Stack sx={{gap: '10px'}}>
            <Stack direction='row' alignItems='center' sx={{gap: '10px', flexWrap: 'wrap'}}>
              <Typography sx={{fontWeight: 400, fontSize: '22px', color: '#1A1916', lineHeight: '100%',
                textDecoration: 'underline', textDecorationColor: '#DA7955', textUnderlineOffset: '5px'}}>
                {routine.title || routine.routineRn}
              </Typography>
              <Box sx={{display: 'inline-flex', alignItems: 'center', px: '8px', py: '4px', borderRadius: '4px',
                border: isActive ? '1px solid #6EE7B7' : '1px solid #E8E4DE',
                backgroundColor: isActive ? '#ECFDF5' : '#F3F2EF'}}>
                <Typography sx={{fontSize: '11px', fontWeight: 400, color: isActive ? '#059669' : '#6B7280', lineHeight: '100%'}}>
                  <Translate expression={isActive ? 'routines.View.Active' : 'routines.View.Inactive'} />
                </Typography>
              </Box>
            </Stack>
            {routine.description && (
              <Typography sx={{fontWeight: 400, fontSize: '14px', color: '#A8A49E', lineHeight: '150%'}}>
                {routine.description}
              </Typography>
            )}
          </Stack>

          <Stack direction='row' sx={{gap: '8px'}}>
            <Button
              onClick={onRunNow}
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#FFFFFF', 'backgroundColor': '#DA7955', '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'}}}
            >
              <Translate expression='routines.View.RunNow' />
            </Button>
            <Button
              onClick={onEdit}
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#FFFFFF', 'backgroundColor': '#DA7955', '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'}}}
            >
              <Translate expression='routines.View.Edit' />
            </Button>
            <Button
              onClick={onToggleStatus}
              variant='outlined'
              disableElevation
              sx={{'minWidth': '0', 'borderRadius': '7px', 'px': '12px', 'py': '8px', 'fontWeight': 500,
                'fontSize': '12px', 'lineHeight': '100%', 'textTransform': 'none',
                'color': '#8E8A84', 'borderColor': '#E8E4DE', 'backgroundColor': '#FFFFFF',
                '&:hover': {backgroundColor: '#FFFFFF', borderColor: '#E8E4DE', boxShadow: 'none'}}}
            >
              <Translate expression={isActive ? 'routines.View.Pause' : 'routines.View.Activate'} />
            </Button>
          </Stack>
        </Stack>

        {/* Schedule */}
        <Stack sx={{gap: '10px'}}>
          <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
            <Translate expression='routines.View.Schedule' />
          </Typography>
          <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
            <Stack direction='row' alignItems='center' sx={{gap: '8px'}}>
              <ScheduleOutlined sx={{fontSize: 14, color: '#A8A49E'}} />
              <Typography sx={{fontSize: '13px', color: '#1A1916', lineHeight: '150%'}}>
                {formatSchedule(routine.schedule)}
              </Typography>
            </Stack>
            {(lastRan || nextRun) && (
              <Stack direction='row' sx={{mt: '10px', gap: '24px', flexWrap: 'wrap'}}>
                {lastRan && (
                  <Stack sx={{gap: '4px'}}>
                    <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#A8A49E', textTransform: 'uppercase'}}>
                      <Translate expression='routines.View.LastRan' />
                    </Typography>
                    <Typography sx={{fontSize: '12px', color: '#6B6760'}}>{lastRan}</Typography>
                  </Stack>
                )}
                {nextRun && (
                  <Stack sx={{gap: '4px'}}>
                    <Typography sx={{fontSize: '10px', fontWeight: 600, color: '#A8A49E', textTransform: 'uppercase'}}>
                      <Translate expression='routines.View.NextRun' />
                    </Typography>
                    <Typography sx={{fontSize: '12px', color: '#6B6760'}}>{nextRun}</Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </Stack>

        {/* Instructions */}
        {routine.instructions && (
          <Stack sx={{gap: '10px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.View.Instructions' />
            </Typography>
            <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
              <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#1A1916', lineHeight: '175%', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                {routine.instructions}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Run history */}
        {history.length > 0 && (
          <Stack sx={{gap: '10px'}}>
            <Typography sx={{fontSize: '11px', fontWeight: 600, color: '#A8A49E', lineHeight: '100%', textTransform: 'uppercase'}}>
              <Translate expression='routines.View.History' />
            </Typography>
            <Box sx={{border: '1px solid #E8E4DE', borderRadius: '12px', px: '14px', py: '12px', backgroundColor: '#FBFBFB'}}>
              <Stack sx={{gap: '12px'}}>
                {history.map((run, i) => {
                  const success = run.status === 'SUCCESS' || run.status === 'COMPLETE' || run.status === 'COMPLETED';
                  const ranAt = formatTs(run.ranAt || run.startedAt || run.createdAt);
                  return (
                    <Stack key={i} direction='row' alignItems='flex-start' sx={{gap: '10px'}}>
                      {success ?
                        <CheckCircleOutlined sx={{fontSize: 14, color: '#10B981', mt: '2px', flexShrink: 0}} /> :
                        <ErrorOutlineOutlined sx={{fontSize: 14, color: '#EF4444', mt: '2px', flexShrink: 0}} />
                      }
                      <Stack sx={{gap: '3px', flex: 1, minWidth: 0}}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{gap: '8px'}}>
                          <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
                            {ranAt || '—'}{run.duration ? ` · ${run.duration}ms` : ''}
                          </Typography>
                          {run.sessionRn && (
                            <Box
                              onClick={() => navigate(`/agent/chat?sessionRn=${run.sessionRn}`)}
                              sx={{'display': 'inline-flex', 'alignItems': 'center', 'gap': '4px',
                                'cursor': 'pointer', 'color': '#A8A49E', '&:hover': {color: '#DA7955'}}}
                            >
                              <ChatOutlined sx={{fontSize: 13}} />
                              <Typography sx={{fontSize: '11px', lineHeight: '100%'}}>View</Typography>
                            </Box>
                          )}
                        </Stack>
                        {run.summary && (
                          <Typography sx={{fontSize: '12px', color: '#1A1916', lineHeight: '150%',
                            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>
                            {run.summary}
                          </Typography>
                        )}
                        {!success && run.error && (
                          <Typography sx={{fontSize: '12px', color: '#EF4444', lineHeight: '150%'}}>{run.error}</Typography>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

RoutinesView.propTypes = {
  routine: PropTypes.shape({
    routineRn: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    instructions: PropTypes.string,
    status: PropTypes.string,
    schedule: PropTypes.object,
    lastRunAt: PropTypes.string,
    nextRunAt: PropTypes.string,
    history: PropTypes.array,
  }),
  onRunNow: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
};

export default React.memo(RoutinesView);
